// Create + edit forms for capture child entities, each rendered in
// the shared EntityDetailSheet. The same component handles create (no `value`)
// and edit (existing view passed in) — fields initialize from the view when the
// sheet opens. Fields the UI doesn't expose yet (impacted_requirements_json,
// response_artifact_id, data_json, ai_drafted) are passed back through unchanged
// so an edit never silently blanks them.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { parseDateInput, timestampToDateInput } from '../../lib/localCalendarDate';
import { CHANNELS, SENTIMENTS, WORKSTREAM_STATUSES } from './constants';
import { DocumentsPanel } from './DocumentsPanel';
import { EntityDetailSheet, LabeledField } from './EntityDetailSheet';
import { useUpsertCustomerInteraction, useUpsertWorkstreamItem } from './hooks';
function tsToInput(ts) {
    return timestampToDateInput(ts);
}
function inputToDate(s) {
    return parseDateInput(s);
}
function OptionSelect({ value, onChange, options, testid }) {
    return /*#__PURE__*/ _jsxs(Select, {
        value: value,
        onValueChange: onChange,
        children: [
            /*#__PURE__*/ _jsx(SelectTrigger, {
                "data-testid": testid,
                children: /*#__PURE__*/ _jsx(SelectValue, {})
            }),
            /*#__PURE__*/ _jsx(SelectContent, {
                children: options.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                        value: o.value,
                        children: o.label
                    }, o.value))
            })
        ]
    });
}
// -------------------------------------------------------- Interaction ----
export function InteractionSheet({ open, onOpenChange, captureProjectId, correlationId, interaction }) {
    const upsert = useUpsertCustomerInteraction(correlationId);
    const [summary, setSummary] = useState('');
    const [channel, setChannel] = useState('meeting');
    const [stakeholder, setStakeholder] = useState('');
    const [sentiment, setSentiment] = useState('unknown');
    const [interactionDate, setInteractionDate] = useState('');
    const [followUps, setFollowUps] = useState('');
    const [notes, setNotes] = useState('');
    useEffect(()=>{
        if (!open) return;
        setSummary(interaction?.summary ?? '');
        setChannel(interaction?.channel || 'meeting');
        setStakeholder(interaction?.stakeholder ?? '');
        setSentiment(interaction?.sentiment || 'unknown');
        setInteractionDate(tsToInput(interaction?.interactionDate));
        setFollowUps(interaction?.followUps ?? '');
        setNotes(interaction?.notes ?? '');
    }, [
        open,
        interaction
    ]);
    const save = ()=>upsert.mutate({
            captureProjectId,
            id: interaction?.id,
            summary: summary.trim(),
            channel,
            stakeholder: stakeholder.trim(),
            sentiment,
            interactionDate: inputToDate(interactionDate),
            followUps,
            notes
        }, {
            onSuccess: ()=>onOpenChange(false)
        });
    return /*#__PURE__*/ _jsxs(EntityDetailSheet, {
        open: open,
        onOpenChange: onOpenChange,
        entity: "interaction",
        title: interaction ? 'Edit interaction' : 'Log interaction',
        description: "A customer-shaping touchpoint — meeting, call, email, or conference.",
        onSave: save,
        saveDisabled: !summary.trim(),
        saving: upsert.isPending,
        children: [
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Summary",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: summary,
                    onChange: (e)=>setSummary(e.target.value),
                    placeholder: "What happened?",
                    "data-testid": Testids.capture.interactionSummaryInput
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(LabeledField, {
                        label: "Channel",
                        children: /*#__PURE__*/ _jsx(OptionSelect, {
                            value: channel,
                            onChange: setChannel,
                            options: CHANNELS,
                            testid: Testids.capture.interactionChannelSelect
                        })
                    }),
                    /*#__PURE__*/ _jsx(LabeledField, {
                        label: "Sentiment",
                        children: /*#__PURE__*/ _jsx(OptionSelect, {
                            value: sentiment,
                            onChange: setSentiment,
                            options: SENTIMENTS
                        })
                    }),
                    /*#__PURE__*/ _jsx(LabeledField, {
                        label: "Stakeholder",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: stakeholder,
                            onChange: (e)=>setStakeholder(e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsx(LabeledField, {
                        label: "Date",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "date",
                            value: interactionDate,
                            onChange: (e)=>setInteractionDate(e.target.value)
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Follow-ups",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    value: followUps,
                    onChange: (e)=>setFollowUps(e.target.value),
                    rows: 2
                })
            }),
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Notes",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    value: notes,
                    onChange: (e)=>setNotes(e.target.value),
                    rows: 2
                })
            }),
            interaction && /*#__PURE__*/ _jsx("div", {
                className: "border-t pt-4",
                children: /*#__PURE__*/ _jsx(DocumentsPanel, {
                    entityType: "interaction",
                    entityId: interaction.id
                })
            })
        ]
    });
}
// --------------------------------------------------------- Workstream ----
export function WorkstreamSheet({ open, onOpenChange, captureProjectId, correlationId, item, workstreamType }) {
    const upsert = useUpsertWorkstreamItem(correlationId);
    const [title, setTitle] = useState('');
    const [status, setStatus] = useState('open');
    const [body, setBody] = useState('');
    useEffect(()=>{
        if (!open) return;
        setTitle(item?.title ?? '');
        setStatus(item?.status || 'open');
        setBody(item?.body ?? '');
    }, [
        open,
        item
    ]);
    const save = ()=>upsert.mutate({
            captureProjectId,
            id: item?.id,
            workstreamType: item?.workstreamType ?? workstreamType,
            title: title.trim(),
            status,
            body,
            // passthrough — not user-editable yet
            dataJson: item?.dataJson ?? '',
            aiDrafted: item?.aiDrafted ?? false
        }, {
            onSuccess: ()=>onOpenChange(false)
        });
    return /*#__PURE__*/ _jsxs(EntityDetailSheet, {
        open: open,
        onOpenChange: onOpenChange,
        entity: "workstream",
        title: item ? 'Edit workstream item' : 'New workstream item',
        onSave: save,
        saveDisabled: !title.trim(),
        saving: upsert.isPending,
        children: [
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Title",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: title,
                    onChange: (e)=>setTitle(e.target.value),
                    placeholder: "e.g. Incumbent vulnerabilities",
                    "data-testid": Testids.capture.workstreamItemTitleInput
                })
            }),
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Status",
                children: /*#__PURE__*/ _jsx(OptionSelect, {
                    value: status,
                    onChange: setStatus,
                    options: WORKSTREAM_STATUSES,
                    testid: Testids.capture.workstreamStatusSelect
                })
            }),
            /*#__PURE__*/ _jsx(LabeledField, {
                label: "Detail",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    value: body,
                    onChange: (e)=>setBody(e.target.value),
                    rows: 6,
                    "data-testid": Testids.capture.workstreamItemBody
                })
            }),
            item && /*#__PURE__*/ _jsx("div", {
                className: "border-t pt-4",
                children: /*#__PURE__*/ _jsx(DocumentsPanel, {
                    entityType: "workstream",
                    entityId: item.id
                })
            })
        ]
    });
}
